import React, { useEffect, useState } from "react";
import {
  BUTTON_VARIANTS,
  Dialog,
  TextArea,
  Input,
  PrimaryButton,
  Card,
  IconInfo,
  RadioGroup,
  StatusIndicator,
  STATUS_INDICATOR_VARIANTS,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
  Loader,
} from "cdk-radial";
import { CircularProgress } from "@cdk-uip/react-circular-progress";

import "./style.css";
import ApisListTable from "./ApisListTable";
import BundleApisTable from "./BundleApisTable";
import { INDICATOR_COLORS, ORG_TYPE_OPTIONS, VISIBILITY_OPTIONS, ORG_TYPE_FILTER } from "../Utils/constants";
import styled from 'styled-components';
import withNavigationToBundles from "./withNavigationToBundles";

const getOrgTypeLabel = (orgType) => {
  if (orgType === "DLR") return "Dealer";
  return orgType || "ISV";
};

const getVisibilityLabel = (visibility) =>
  visibility === "private" ? "Private" : "Public";

const CUSTOM_STATUS_INDICATOR_VARIANT =
  STATUS_INDICATOR_VARIANTS.CUSTOM ?? STATUS_INDICATOR_VARIANTS.DEFAULT;

const getIndicatorColorsForOrgType = (orgType) =>
  INDICATOR_COLORS[orgType] || INDICATOR_COLORS.ISV;

const StyledCreateBundleConfirmationDialog = styled(Dialog)`
  width: 40vw;
  height: 408px;
`;

function Create({ auth, reservedBundles, getApiList, createBundle, setBundleCreateError, ApiBundleManagementReducer }) {
  const [bundleName, setBundleName] = useState("");
  const [bundleDescription, setBundleDescription] = useState("");
  const [bundleNameErrorMessage, setBundleNameErrorMessage] = useState("");
  const [bundleDescriptionErrorMessage, setBundleDescriptionErrorMessage] =
    useState("");

  const [selectedApis, setSelectedApis] = useState([]);

  const [hasNameError, setHasNameError] = useState(false);
  const [hasDescError, setHasDescError] = useState(false);
  const [orgType, setOrgType] = useState("ISV");
  const [visibility, setVisibility] = useState("public");
  const [isVisibilityDisabled, setIsVisibilityDisabled] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { accessToken } = auth;
  const {
    apis,
    fetchingApis,
    errorFetchingApis,
    creatingBundle,
    bundleCreateError,
    bundleCreateErrorMessage
  } = ApiBundleManagementReducer;
  const tooltipProps = {
    id: "info-icon-tooltip",
    styles: { width: "350px", maxWidth: "350px" },
    text: "Your bundle is automatically private if it has Private or Request Only APIs in it",
    delay: 0,
    theme: THEMES.LIGHT,
  };

  const ComponentToRender = ({ ...props }) => {
    return <IconInfo {...props} />;
  };

  const ComponentWithTooltip = withTooltip(ComponentToRender, tooltipProps);

  useEffect(() => {
    getApiList({ accessToken });
  }, []);

  useEffect(() => {
    setVisibility(isVisibilityDisabled ? "private" : "public");
  }, [isVisibilityDisabled]);

  const handleBundleNameChange = (event) => {
    setBundleName(event.target.value);
  };
  const handleBundleNameBlur = (e) => {
    if (Boolean(e?.target?.value)) {
      if (reservedBundles.find((b) => b === e?.target?.value)) {
        setBundleNameErrorMessage("Bundle name already exist");
        setHasNameError(true);
      } else {
        setBundleNameErrorMessage("");
        setHasNameError(false);
      }
    } else {
      setBundleNameErrorMessage("please add Bundle name");
      setHasNameError(true);
    }
  };
  const onDescriptionChange = (event) => {
    setBundleDescription(event.target.value);
  };
  const onDescriptionBlur = (e) => {
    if (Boolean(e?.target?.value)) {
      setBundleDescriptionErrorMessage("");
      setHasDescError(false);
    } else {
      setBundleDescriptionErrorMessage("please add Bundle description");
      setHasDescError(true);
    }
  };

  const handleCreateBundle = () => {
    setIsDialogOpen(true);
  };

  const handleOrgTypeChange = (event) => {
    setOrgType(event.target.value);
  };

  const handleVisibilityChange = (event) => {
    if (isVisibilityDisabled) return;
    setVisibility(event.target.value);
  };

  const handleCreateBundleConfirm = () => {
    const apis = selectedApis.map((api) => {
      return {
        id: api?.api_id,
        type: api?.api_type === "Rest" ? "api" : "async-api",
      };
    });

    setIsDialogOpen(false);
    createBundle({
      accessToken,
      bundleData: {
        name: bundleName,
        description: bundleDescription,
        status: "inactive",
        visibility,
        orgType,
        apis: apis,
      },
    });
  };

  useEffect(() => {
    if (selectedApis?.some(api => ORG_TYPE_FILTER[orgType](api))) {
      setVisibility("private");
      setIsVisibilityDisabled(true);
    } else {
      setIsVisibilityDisabled(false);
    }
  }, [orgType, selectedApis]);

  return (
    <>
      {errorFetchingApis ? (
        <div className="error-div">Something went wrong, please try again.</div>
      ) : (
        <>
          {creatingBundle ? (
            <div className="loading-container">
              <h3>Creating Bundle ...</h3>
              <CircularProgress />
            </div>
          ) : (
            <Card>
              <div className="create-bundle-card">
                <div className="create-bundle-title">Create a bundle</div>
                <Input
                  dataTestId="input-api-bundle-name"
                  enableCustomValidation={true}
                  enableIsRequiredValidation
                  errorMessage={bundleNameErrorMessage}
                  id="api-name-input"
                  hasError={hasNameError}
                  label="Bundle Name"
                  name={"api-name-input-panel"}
                  isRequired
                  value={bundleName}
                  onBlur={handleBundleNameBlur}
                  onChange={handleBundleNameChange}
                />
                <TextArea
                  enableCustomValidation
                  id="description-input"
                  isRequired
                  label="Bundle Description"
                  maxLength="500"
                  name="description"
                  placeholder="Add Bundle Description"
                  onBlur={onDescriptionBlur}
                  onChange={onDescriptionChange}
                  value={bundleDescription}
                  hasError={hasDescError}
                  errorMessage={bundleDescriptionErrorMessage}
                  hasCharacterCount
                />

                <div className="bundle-radio-row">
                  <div className="bundle-radio-group">
                    <div className="bundle-review-field-label">Org Type</div>
                    <RadioGroup
                      dataTestId="org-type-radio-group"
                      id="org-type-radio-group"
                      name="org-type"
                      onChange={handleOrgTypeChange}
                      options={ORG_TYPE_OPTIONS}
                      value={orgType}
                    />
                  </div>

                  <div className="bundle-radio-group">
                    <div
                      dataTestId="bundle-visibility"
                      className="bundle-review-field-label"
                    >
                      Visibility
                    </div>
                    <div className="bundle-visibility-row">
                      <div
                        className={
                          isVisibilityDisabled
                            ? "bundle-visibility-disabled"
                            : undefined
                        }
                        aria-disabled={isVisibilityDisabled}
                      >
                        <RadioGroup
                          dataTestId="bundle-visibility-radio-group"
                          id="bundle-visibility-radio-group"
                          name="visibility"
                          onChange={handleVisibilityChange}
                          options={VISIBILITY_OPTIONS}
                          value={visibility}
                        />
                      </div>
                      <ComponentWithTooltip
                        horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
                        verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.TOP}
                      />
                    </div>
                  </div>
                </div>

                {!!fetchingApis ? (
                  <Loader
                    dataTestId="fectching-apis-loader"
                    size="small"
                    label="Fetching Apis..."
                  />
                ) : (
                  <>
                    <div>
                      <ApisListTable apis={selectedApis} isViewOnly={true} />
                    </div>

                    <div>
                      <ApisListTable
                        apis={apis}
                        setSelectedApis={setSelectedApis}
                        isViewOnly={false}
                      />
                    </div>
                  </>
                )}

                <div className="api-details-panel-save-button">
                  <PrimaryButton
                    dataTestId="create-bundle"
                    text="Create Bundle"
                    onClick={handleCreateBundle}
                    isDisabled={
                      !(
                        !!bundleName &&
                        !hasNameError &&
                        !!bundleDescription &&
                        selectedApis?.length
                      )
                    }
                  />
                </div>
                {bundleCreateError ? (
                  <Dialog
                    className="create-api-dialog"
                    buttonsProps={[
                      {
                        id: "cancel-action",
                        onClick: () => setBundleCreateError(),
                        text: "Cancel",
                        variant: BUTTON_VARIANTS.SECONDARY,
                      },
                    ]}
                    id="share-api-dialog"
                    isOpen={bundleCreateError}
                    onClose={() => setBundleCreateError()}
                    title="Bundle Creation Failed"
                  >
                    {!!bundleCreateError && (
                      <div>{bundleCreateErrorMessage}</div>
                    )}
                  </Dialog>
                ) : null}
              </div>
            </Card>
          )}
        </>
      )}
      {!!isDialogOpen && (
        <StyledCreateBundleConfirmationDialog
          buttonsProps={[
            {
              id: "dialog-action-1",
              onClick: () => setIsDialogOpen(false),
              text: "Cancel",
              variant: BUTTON_VARIANTS.SECONDARY,
            },
            {
              id: "dialog-action-2",
              onClick: handleCreateBundleConfirm,
              text: "Confirm",
              variant: BUTTON_VARIANTS.PRIMARY,
            },
          ]}
          dataTestId="bundle-create-dialog"
          id="bundle-create-dialog"
          onClose={() => setIsDialogOpen(false)}
          title="Review Bundle Details"
          isOpen={isDialogOpen}
        >
          <div className="bundle-review-dialog">
            <div className="bundle-review-dialog-text">
              Are you sure you want to add these {selectedApis?.length || 0}{" "}
              APIs into a bundle? These APIs will immediately be available to
              any organization that has access to this bundle.
            </div>

            <div className="bundle-review-summary">
              <div className="bundle-review-summary-name">{bundleName}</div>
              <div className="bundle-review-summary-indicators">
                <StatusIndicator
                  label={`${getOrgTypeLabel(orgType)} : ${getVisibilityLabel(
                    visibility,
                  )}`}
                  ariaLabel={`Bundle ${getOrgTypeLabel(
                    orgType,
                  )} ${getVisibilityLabel(visibility)}`}
                  variant={CUSTOM_STATUS_INDICATOR_VARIANT}
                  className="bundle-review-status-indicator"
                  style={{
                    borderColor:
                      getIndicatorColorsForOrgType(orgType).borderColor,
                    backgroundColor:
                      getIndicatorColorsForOrgType(orgType).backgroundColor,
                  }}
                />
              </div>
            </div>

            <BundleApisTable selectedApis={selectedApis} />
          </div>
        </StyledCreateBundleConfirmationDialog>
      )}
    </>
  );
}

export default withNavigationToBundles(Create);
